import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layers, Plus, Trash2, Edit, Zap, Lock, Info, AlertTriangle, Shield, Tag } from 'lucide-react';
import { listSegments, deleteSegment, isSegmentUsable, type Segment } from '../../lib/supabase';
import { SPORTSCHULEN } from '../../lib/sportschulen';
import { GruppenFilter } from '../../components/GruppenFilter/GruppenFilter';
import { ARTEN, LEERER_FILTER, alterText, gruppenInfo, passt, type FilterWert } from '../../components/GruppenFilter/gruppenInfo';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { formatDateTime, formatNumber } from '../../lib/format';
import { useAuthStore } from '../../stores/authStore';

export function SegmentList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canEdit = user?.role === 'admin' || user?.role === 'redakteur' || user?.role === 'studio_leiter';

  const { data, isLoading } = useQuery({ queryKey: ['segments'], queryFn: listSegments });
  const [filter, setFilter] = useState<FilterWert>(LEERER_FILTER);
  const infos = useMemo(() => new Map((data ?? []).map((g) => [g.id, gruppenInfo(g)])), [data]);
  const sichtbar = useMemo(() => (data ?? []).filter((g) => passt(g, filter, infos.get(g.id))), [data, filter, infos]);
  const ohneAlter = filter.alter ? (data ?? []).filter((g) => !infos.get(g.id)?.alter && passt(g, { ...filter, alter: null }, infos.get(g.id))).length : 0;
  const arten = ARTEN.map((a) => a.id).filter((a) => (data ?? []).some((g) => infos.get(g.id)?.art === a));

  const deleteMutation = useMutation({
    mutationFn: deleteSegment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['segments'] }),
  });

  const handleDelete = (s: Segment) => {
    if (!confirm(`Gruppe "${s.name}" wirklich löschen?`)) return;
    deleteMutation.mutate(s.id);
  };

  return (
    <div>
      <PageHeader
        title="Gruppen"
        subtitle="Empfänger-Gruppen für Kampagnen"
        actions={canEdit && (<Link to="/segments/new" className="btn btn-primary"><Plus size={16} /> Neue Gruppe</Link>)}
      />

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <Info size={16} />
        <div>
          <strong>Was Gruppen sind:</strong> Empfänger-Listen für Kampagnen.
          {' '}<strong>Dynamisch</strong> = automatisch nach Filtern (z.B. „alle Kontakte aus Berlin"),
          aktualisiert sich nachts mit dem CRM. <strong>Statisch</strong> = handverlesene Kontakte, ändert sich nicht von allein.
          Ein Kontakt kann in mehreren Gruppen gleichzeitig sein.
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="Noch keine Gruppen"
          description="Gruppen bestimmen, wer eine Kampagne erhält. Lege eine dynamische Gruppe (per Filter) oder statische Gruppe (manuelle Auswahl) an."
          action={canEdit && (<Link to="/segments/new" className="btn btn-primary"><Plus size={16} /> Erste Gruppe anlegen</Link>)}
        />
      ) : (
        <>
        <GruppenFilter
          id="gruppen-filter"
          wert={filter}
          onChange={setFilter}
          standard={LEERER_FILTER}
          schulen={SPORTSCHULEN.map((x) => x.name)}
          mitUebergreifend
          arten={arten}
          treffer={sichtbar.length}
          gesamt={data.length}
          ohneAlter={ohneAlter}
        />
        {sichtbar.length === 0 ? (
          <div className="alert alert-info"><Info size={16} /><div>Keine Gruppe passt zu Suche und Filter. <button type="button" className="zf-aendern" onClick={() => setFilter(LEERER_FILTER)}>Filter zurücksetzen</button></div></div>
        ) : (
        <div style={{ overflowX: 'auto', position: 'relative' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th><th>Typ</th><th>Sichtbarkeit</th><th>Gültig bis</th><th>Kontakte</th><th>Aktualisiert</th>
              <th style={{ width: '140px', textAlign: 'right' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {sichtbar.map(s => {
              const expired = !isSegmentUsable(s);
              const info = infos.get(s.id);
              return (
                <tr key={s.id} className="clickable" onClick={() => navigate(`/segments/${s.id}`)}
                  style={{ opacity: expired ? 0.65 : 1 }}>
                  <td>
                    <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      {s.name}
                      {s.is_favorite && <span title="Favorit" style={{ color: 'var(--warning-dark, #b25e00)' }}>⭐</span>}
                      {s.always_included && <span className="status-badge" title="Bekommt jede Mail automatisch in Kopie" style={{ background: 'var(--info-light)', color: 'var(--info-dark)', fontSize: '0.65rem' }}>immer dabei</span>}
                      {s.crm_tag_slug && <span className="status-badge" title="Stündlich aus dem CRM-Tag" style={{ background: 'var(--info-light)', color: 'var(--info-dark)', fontSize: '0.65rem' }}><Tag size={10} /> CRM-Tag</span>}
                      {info?.alter && <span className="status-badge" style={{ background: 'var(--surface-secondary)', color: 'var(--text-secondary)', fontSize: '0.65rem' }}>{alterText(info.alter)}</span>}
                    </div>
                    {s.description && (<div style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)', marginTop: '2px' }}>{s.description}</div>)}
                  </td>
                  <td>
                    <span className="status-badge" style={{
                      background: s.is_dynamic ? 'var(--info-light)' : 'var(--surface-secondary)',
                      color: s.is_dynamic ? 'var(--info-dark)' : 'var(--text-secondary)',
                    }}>
                      {s.is_dynamic ? <><Zap size={11} /> dynamisch</> : <><Lock size={11} /> statisch</>}
                    </span>
                  </td>
                  <td>
                    {s.admin_only ? (
                      <span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)' }}>
                        <Shield size={11} /> Admin-only
                      </span>
                    ) : s.studio_scope && s.studio_scope.length > 0 ? (
                      <span className="status-badge" style={{ background: 'var(--info-light)', color: 'var(--info-dark)' }} title={s.studio_scope.join(', ')}>
                        {s.studio_scope.length} {s.studio_scope.length === 1 ? 'Studio' : 'Studios'}
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>global</span>
                    )}
                  </td>
                  <td style={{ fontSize: '0.8125rem' }}>
                    {!s.valid_until ? (
                      <span style={{ color: 'var(--text-tertiary)' }}>unbegrenzt</span>
                    ) : expired ? (
                      <span className="status-badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-dark)' }}>
                        <AlertTriangle size={11} /> abgelaufen
                      </span>
                    ) : (
                      new Date(s.valid_until).toLocaleDateString('de-DE')
                    )}
                  </td>
                  <td><strong>{formatNumber(s.contact_count)}</strong></td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{formatDateTime(s.updated_at)}</td>
                  <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/segments/${s.id}`)}><Edit size={14} /></button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(s)}><Trash2 size={14} /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        )}
        </>
      )}
    </div>
  );
}
